import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';
const PAGE_URL = `${SITE_URL}/service-areas/marion-county`;

const marionCities = [
  {
    name: 'Indianapolis',
    slug: 'indianapolis',
    population: '880,000+',
    highlight: 'Indiana\'s capital & largest city',
    zips: ['46201', '46202', '46204', '46208', '46220', '46240', '46250', '46256', '46260'],
    icon: 'ri-building-2-line',
    desc: 'Comprehensive life insurance for Indianapolis families, Eli Lilly employees, IU Health workers, and all 15 metro neighborhoods.',
  },
  {
    name: 'Lawrence',
    slug: 'lawrence',
    population: '50,000+',
    highlight: 'Northeast Marion County',
    zips: ['46216', '46219', '46226', '46235', '46236'],
    icon: 'ri-community-line',
    desc: 'Serving Lawrence and the northeast Indianapolis corridor with term life, whole life, and mortgage protection coverage.',
  },
  {
    name: 'Castleton',
    slug: 'castleton',
    population: '30,000+',
    highlight: 'ZIP codes 46250, 46256 & 46240',
    zips: ['46240', '46250', '46256'],
    icon: 'ri-home-4-line',
    desc: 'Hyper-local life insurance for northeast Indianapolis families in Castleton, Keystone at the Crossing, and surrounding communities.',
  },
  {
    name: 'Broad Ripple',
    slug: 'broad-ripple',
    population: '15,000+',
    highlight: 'Young professional hub',
    zips: ['46220', '46205'],
    icon: 'ri-cup-line',
    desc: 'Affordable coverage for Broad Ripple\'s young professionals, renters, and growing families — term life from under $20/month.',
  },
  {
    name: 'Beech Grove',
    slug: 'beech-grove',
    population: '15,000+',
    highlight: 'South-central Marion County',
    zips: ['46107'],
    icon: 'ri-map-pin-line',
    desc: 'Trusted life insurance and mortgage protection for Beech Grove families and Amtrak workers in the heart of south Indianapolis.',
  },
  {
    name: 'Speedway',
    slug: 'speedway',
    population: '12,000+',
    highlight: 'Home of the Indianapolis 500',
    zips: ['46224'],
    icon: 'ri-speed-line',
    desc: 'Personalized life insurance for Speedway residents, motorsport industry families, and homeowners near the IMS corridor.',
  },
];

const stats = [
  { value: '960,000+', label: 'Marion County Residents', icon: 'ri-group-line' },
  { value: '6', label: 'Communities Served', icon: 'ri-map-pin-2-line' },
  { value: '20+', label: 'A-Rated Carriers', icon: 'ri-shield-star-line' },
  { value: '$0', label: 'Cost to Get a Quote', icon: 'ri-money-dollar-circle-line' },
];

export default function MarionCountyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Life Insurance Marion County Indiana | Hoosier Life';

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Life insurance for Marion County, Indiana — serving Indianapolis, Lawrence, Castleton, Broad Ripple, Beech Grove & Speedway. Independent agent, 20+ carriers, free quotes.');
    setMeta('keywords', 'life insurance Marion County Indiana, Indianapolis life insurance, Marion County insurance agent, Lawrence Indiana life insurance, Castleton life insurance');
    setMeta('geo.region', 'US-IN');
    setMeta('geo.placename', 'Marion County, Indiana');
    setMeta('geo.position', '39.7684;-86.1581');
    setMeta('last-modified', new Date().toISOString());
    setMeta('og:title', 'Life Insurance Marion County Indiana | Hoosier Life Insurance', true);
    setMeta('og:description', 'Independent life insurance agent serving all of Marion County, Indiana — Indianapolis, Lawrence, Castleton, Broad Ripple, Beech Grove & Speedway.', true);
    setMeta('og:url', PAGE_URL, true);
    setMeta('og:type', 'website', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', PAGE_URL);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${PAGE_URL}#webpage`,
        url: PAGE_URL,
        name: 'Life Insurance in Marion County, Indiana | Hoosier Life Insurance',
        description: 'Hoosier Life Insurance serves all of Marion County, Indiana — Indianapolis, Lawrence, Castleton, Broad Ripple, Beech Grove, and Speedway. Independent agent, 20+ A-rated carriers.',
        inLanguage: 'en-US',
        datePublished: '2024-01-15T00:00:00-05:00',
        dateModified: new Date().toISOString(),
        isPartOf: { '@id': `${SITE_URL}/#website` },
        breadcrumb: { '@id': `${PAGE_URL}#breadcrumb` },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${PAGE_URL}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Service Areas', item: `${SITE_URL}/service-areas` },
          { '@type': 'ListItem', position: 3, name: 'Marion County', item: PAGE_URL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'InsuranceAgency',
        '@id': `${SITE_URL}/#organization`,
        name: 'Hoosier Life Insurance',
        description: 'Independent life insurance agency serving all of Marion County and Central Indiana.',
        url: SITE_URL,
        telephone: '+13176482983',
        areaServed: [
          { '@type': 'City', name: 'Indianapolis', containedInPlace: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Lawrence', containedInPlace: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Castleton', containedInPlace: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Broad Ripple', containedInPlace: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Beech Grove', containedInPlace: { '@type': 'AdministrativeArea', name: 'Marion County' } },
          { '@type': 'City', name: 'Speedway', containedInPlace: { '@type': 'AdministrativeArea', name: 'Marion County' } },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${PAGE_URL}#citylist`,
        name: 'Life Insurance Service Areas in Marion County, Indiana',
        numberOfItems: 6,
        itemListElement: marionCities.map((city, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `Life Insurance in ${city.name}, Indiana`,
          url: `${SITE_URL}/service-areas/${city.slug}`,
          description: city.desc,
        })),
      },
    ];

    schemas.forEach((schema, idx) => {
      const id = `schema-marion-county-${idx}`;
      let el = document.getElementById(id) as HTMLScriptElement | null;
      if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
      el.textContent = JSON.stringify(schema);
    });

    return () => {
      [0, 1, 2, 3].forEach(idx => {
        document.getElementById(`schema-marion-county-${idx}`)?.remove();
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-[#D97642] transition-colors">
            <i className="ri-home-4-line text-base"></i>
            <span>Home</span>
          </Link>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <Link to="/service-areas" className="text-white/70 hover:text-[#D97642] transition-colors">Service Areas</Link>
          <i className="ri-arrow-right-s-line text-white/40"></i>
          <span className="text-[#D97642] font-semibold">Marion County</span>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-[#D97642]/6 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-white/4 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#D97642]/20 border border-[#D97642]/40 text-[#D97642] text-xs font-semibold uppercase tracking-widest rounded-full mb-6">
              <i className="ri-map-pin-2-line"></i>
              Marion County, Indiana
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
              Life Insurance in<br />
              <span className="text-[#D97642]">Marion County</span>,<br />
              Indiana
            </h1>
            <p className="text-lg text-gray-300 mb-4 leading-relaxed max-w-2xl">
              Hoosier Life Insurance serves all of Marion County — from downtown Indianapolis to Lawrence, Castleton, Broad Ripple, Beech Grove, and Speedway. As an independent agent working with 20+ A-rated carriers, we find the right coverage at the best price for every Marion County family.
            </p>
            <p className="text-base text-gray-400 mb-8 leading-relaxed max-w-2xl">
              Marion County is Indiana's most populous county, with over 960,000 residents across a diverse mix of urban neighborhoods, established suburbs, and tight-knit communities. Every family here has different income levels, mortgage amounts, and health profiles — which is exactly why working with an independent agent who shops all carriers on your behalf makes a real difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/book-appointment"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap"
              >
                <i className="ri-calendar-check-line mr-2"></i>
                Book Free Consultation
              </Link>
              <a
                href="tel:+13176482983"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="py-8 bg-[#1A3A52]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-9 h-9 flex items-center justify-center mx-auto mb-2">
                  <i className={`${s.icon} text-[#D97642] text-2xl`}></i>
                </div>
                <div className="text-white font-black text-2xl">{s.value}</div>
                <div className="text-white/60 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prose intro — crawlable content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="inline-block px-4 py-1.5 bg-[#F9F7F4] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-6">Why Marion County Families Choose Us</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-6 leading-tight">
                Independent Life Insurance Coverage Across All of Marion County
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-[15px]">
                <p>
                  Marion County encompasses Indianapolis and six distinct communities — each with its own character, cost of living, and insurance needs. A family in Castleton paying a $380,000 mortgage needs different coverage than a senior in Beech Grove focused on final expense planning, or a young professional in Broad Ripple looking for affordable term life under $25/month.
                </p>
                <p>
                  As an <strong className="text-gray-900">independent insurance agent</strong>, we are not tied to any single carrier. We compare quotes from Mutual of Omaha, Nationwide, Pacific Life, Prudential, Banner Life, and 15+ other A-rated insurers to find the policy that fits your exact situation. Marion County residents consistently save 20–40% compared to going directly to a single carrier.
                </p>
                <p>
                  <strong className="text-gray-900">Indiana's no state income tax on life insurance death benefits</strong> makes permanent life insurance especially attractive for Marion County families building generational wealth. Combined with the Indiana Life &amp; Health Guaranty Association's protection (up to $300,000 in coverage), your policy is backed by both carrier ratings and state regulation.
                </p>
                <p>
                  We serve clients across Marion County through phone consultations, video meetings, and in-home presentations — whatever is most convenient for your schedule. Most clients complete their quote, application, and approval within 48–72 hours.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { icon: 'ri-shield-check-line', title: 'Licensed Indiana Agent', body: 'State-licensed, fully independent — not captive to any single insurer. We work for you, not a carrier.' },
                { icon: 'ri-exchange-line', title: 'Shop 20+ Carriers at Once', body: 'Mutual of Omaha, Nationwide, Pacific Life, Prudential, Banner Life, North American, and more — compared simultaneously.' },
                { icon: 'ri-home-heart-line', title: 'In-Home or Virtual Consultations', body: 'We come to your Marion County home or meet by video — no office visit required, no pressure.' },
                { icon: 'ri-time-line', title: 'Fast Approvals (24–48 Hours)', body: 'No-exam options available. Most Marion County applicants get approved within one to two business days.' },
                { icon: 'ri-price-tag-3-line', title: 'Free Quote — No Obligation', body: 'See your exact rate in minutes with no sales pressure and no commitment to buy.' },
                { icon: 'ri-heart-pulse-line', title: 'Health Conditions Welcome', body: 'We specialize in placing clients with controlled diabetes, high blood pressure, and other conditions that some agents won\'t touch.' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-[#F9F7F4] rounded-xl border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D97642]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#D97642] text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A3A52] text-sm mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-xs leading-relaxed">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* City grid — all 6 Marion County cities */}
      <section className="py-20 bg-[#F9F7F4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-white text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">All Marion County Communities</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">Life Insurance by City — Marion County, Indiana</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Select your city for local coverage details, neighborhood-specific insights, and tailored policy recommendations.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {marionCities.map((city) => (
              <Link
                key={city.slug}
                to={`/service-areas/${city.slug}`}
                className="group bg-white rounded-2xl p-7 border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#D97642]/10 rounded-xl group-hover:bg-[#D97642] transition-colors">
                    <i className={`${city.icon} text-[#D97642] text-2xl group-hover:text-white`}></i>
                  </div>
                  <span className="text-xs font-semibold text-[#D97642] bg-[#D97642]/10 px-3 py-1 rounded-full whitespace-nowrap">{city.population}</span>
                </div>
                <h3 className="text-xl font-bold text-[#1A3A52] mb-1 group-hover:text-[#D97642] transition-colors">
                  Life Insurance in {city.name}
                </h3>
                <p className="text-xs text-gray-400 font-semibold mb-3 uppercase tracking-wide">{city.highlight}</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-4">{city.desc}</p>
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {city.zips.slice(0, 4).map(z => (
                    <span key={z} className="text-xs bg-[#F9F7F4] text-gray-600 font-mono px-2 py-0.5 rounded border border-gray-200">{z}</span>
                  ))}
                  {city.zips.length > 4 && (
                    <span className="text-xs text-gray-400">+{city.zips.length - 4} more</span>
                  )}
                </div>
                <span className="flex items-center gap-1.5 text-[#D97642] font-semibold text-sm group-hover:gap-3 transition-all">
                  View {city.name} Coverage <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Products section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-[#F9F7F4] text-[#1A3A52] text-xs font-semibold uppercase tracking-widest rounded-full border border-gray-200 mb-4">Coverage Options</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-4">Life Insurance Products for Marion County Families</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Every product below is available to Marion County residents — we'll help you choose the right fit.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
            {[
              { to: '/term-life', icon: 'ri-calendar-check-line', title: 'Term Life', desc: 'Affordable coverage for 10–30 years. Best for families with mortgages.' },
              { to: '/whole-life', icon: 'ri-infinity-line', title: 'Whole Life', desc: 'Lifetime protection + cash value growth. Builds wealth tax-deferred.' },
              { to: '/final-expense', icon: 'ri-hand-heart-line', title: 'Final Expense', desc: 'Guaranteed acceptance for seniors 50–85. No medical exam ever.' },
              { to: '/mortgage-protection', icon: 'ri-home-heart-line', title: 'Mortgage Protection', desc: 'Protects your Marion County home if the worst happens.' },
              { to: '/annuities', icon: 'ri-line-chart-line', title: 'Annuities', desc: 'Guaranteed retirement income. No market risk on principal.' },
            ].map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="group bg-[#F9F7F4] rounded-xl p-6 border border-gray-200 hover:border-[#D97642] hover:shadow-lg transition-all text-center"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl mx-auto mb-4 group-hover:bg-[#D97642] transition-colors">
                  <i className={`${p.icon} text-[#D97642] text-2xl group-hover:text-white`}></i>
                </div>
                <h3 className="font-bold text-[#1A3A52] mb-2 text-sm group-hover:text-[#D97642] transition-colors">{p.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{p.desc}</p>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/all-services" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1A3A52] text-white text-sm font-semibold rounded-lg hover:bg-[#0F2A3F] transition-colors whitespace-nowrap">
              <i className="ri-apps-line"></i>
              See All Coverage Options
            </Link>
          </div>
        </div>
      </section>

      {/* Marion County zip codes */}
      <section className="py-16 bg-[#0F2A3F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-3">Marion County ZIP Codes We Serve</h2>
            <p className="text-white/60 max-w-xl mx-auto text-sm">If your ZIP code is listed below, we can help. Don't see yours? Call us — we likely still serve your area.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5 mb-8">
            {marionCities.map((city) => (
              <div key={city.slug} className="bg-white/5 border border-white/10 rounded-xl p-5">
                <h3 className="text-white font-bold text-sm mb-3 flex items-center gap-2">
                  <i className="ri-map-pin-2-line text-[#D97642]"></i>
                  {city.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {city.zips.map(z => (
                    <span key={z} className="text-xs bg-white/10 text-blue-200 font-mono px-2 py-1 rounded">{z}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-6 py-3 bg-[#D97642] text-white font-semibold rounded-lg hover:bg-[#C26638] transition-colors whitespace-nowrap">
              <i className="ri-phone-line"></i>
              Call (317) 648-2983 — Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3A52] mb-6">Serving Every Corner of Marion County</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Marion County covers 396 square miles and is home to Indiana's most diverse population. We consult by phone, video, or in-home — so wherever you are in Marion County, we can meet you where you are.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Phone & video consultations available Mon–Fri 9AM–5PM, Sat 8AM–1PM',
                  'In-home presentations available throughout Marion County',
                  'Same-day quotes for most applicants — no waiting weeks',
                  'No-exam policies available for approved applicants up to $1M',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm text-gray-700">
                    <i className="ri-check-line text-[#D97642] flex-shrink-0"></i>
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/book-appointment" className="inline-flex items-center justify-center px-7 py-3.5 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap">
                  <i className="ri-calendar-check-line mr-2"></i>
                  Book Free Consultation
                </Link>
                <Link to="/instant-quote" className="inline-flex items-center justify-center px-7 py-3.5 bg-white border-2 border-[#1A3A52] text-[#1A3A52] font-semibold rounded-lg hover:bg-[#F9F7F4] transition-all whitespace-nowrap">
                  <i className="ri-calculator-line mr-2"></i>
                  Get Instant Quote
                </Link>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-gray-200 h-[420px]">
              <iframe
                title="Marion County, Indiana Life Insurance Service Area"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d196281.1!2d-86.2520607!3d39.7683965!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x886b50ffa7796a03%3A0xd68e9df640b9ea7c!2sMarion%20County%2C%20IN!5e0!3m2!1sen!2sus!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#0F1F2E] via-[#1A3A52] to-[#0F1F2E]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to Protect Your Marion County Family?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Get a free, no-pressure quote from an independent agent who lives and works in Central Indiana. We shop 20+ carriers so you get the best rate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/book-appointment" className="inline-flex items-center justify-center px-8 py-4 bg-[#D97642] text-white font-bold rounded-lg hover:bg-[#C26638] transition-all whitespace-nowrap">
              <i className="ri-calendar-check-line mr-2"></i>
              Book Free Consultation
            </Link>
            <a href="tel:+13176482983" className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>
              (317) 648-2983
            </a>
          </div>
          <div className="flex flex-wrap justify-center gap-4 text-white/40 text-xs">
            <span className="flex items-center gap-1"><i className="ri-service-areas-line"></i> All 6 Marion County cities</span>
            <span>·</span>
            <span>Mon–Fri 9AM–5PM · Sat 8AM–1PM</span>
            <span>·</span>
            <Link to="/service-areas" className="hover:text-white/70 transition-colors">View all Indiana service areas</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
